<template>
  <div>
    <div class="relative bg-gray-lighter w-full h-[640px]">
      <div class="m-auto p-5 text-center lg:w-1/2">
        <h1 class="capitalize">Find an Installation Guide</h1>
        <div class="mb-4">
          Use the search bar below to find the installation guide and videos available for your product. After choosing
          a product, you may need to select your vehicle information to see the guide specific to your truck.
        </div>
        <div class="relative m-auto w-full max-w-[430px]">
          <div id="installSearchInput" class="scroll-my-80 relative">
            <BaseForm>
              <BaseFormInput
                id="searchBar"
                v-model="searchQuery"
                type="text"
                placeholder="Search by Part Number or Product Name"
                autocomplete="off"
                aria-label="Search"
                data-testid="searchBar"
                style="height: 45px"
                @keyup.esc="resetSearch()"
              />
              <span
                class="absolute top-0 right-0 h-10 w-10 inline-flex items-center justify-center text-right pointer-events-auto z-0"
              >
                <SearchIcon v-if="$device.isDesktop" class="w-4 h-4" />
                <CloseIcon
                  v-else-if="!$device.isDesktop && searchQuery.length > 0"
                  class="w-4 h-4"
                  @click="resetSearch()"
                />
              </span>
            </BaseForm>
          </div>
          <InstallationInstructionsResults :search-query="searchQuery" @close="resetSearch()" />
        </div>
        <p class="my-2">
          Click here for
          <UniversalLink link="/warranty-registration/"> | | Warranty Registration </UniversalLink>
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import SearchIcon from '@/assets/search.svg?component'
import CloseIcon from '@/assets/x.svg?component'

const { $sitewideConfig, $device, $scrollTo } = useNuxtApp()
const { translateTemplate } = useTemplateTranslator()
const route = useRoute()

const searchQuery = ref('')
const title = translateTemplate('Truck Accessories Installation Instructions & Guides [TITLE_SUFFIX]')
const description = `Search for your truck's part or accessory to find a full library of installation instruction guides and manuals`

if (!$sitewideConfig.config.installGuideSearchEnabled)
  throw createError({ statusCode: 404, statusMessage: 'Invalid URL', fatal: true })

function webPageSchema() {
  return {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    url: `https://${$sitewideConfig.domain}${route.path}`,
    headline: title,
    description,
    publisher: {
      '@type': 'Organization',
      name: $sitewideConfig.config.siteName,
    },
  }
}

useServerHead({
  script: [{ type: 'application/ld+json', innerHTML: webPageSchema(), tagPosition: 'bodyClose' }],
})

useMetaTags({
  title,
  description,
})

watch(searchQuery, (newVal) => {
  if ($device.value.isMobile && newVal.length > 0) {
    // Browser scroll behavipr was interupting our scroll so we need to set cancelable to true
    // We set force to false so that it does not scroll if already in view
    $scrollTo('#installSearchInput', 200, { cancelable: false, force: false, offset: -20 })
  }
})

function resetSearch() {
  searchQuery.value = ''
}
</script>
